import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogOut, LayoutDashboard, ReceiptText, Target, Settings as SettingsIcon, Wallet, Sparkles, Crown } from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/expenses", label: "Expenses", icon: ReceiptText, testid: "nav-expenses" },
  { to: "/income", label: "Income", icon: Wallet, testid: "nav-income" },
  { to: "/budgets", label: "Budgets", icon: Target, testid: "nav-budgets" },
  { to: "/goals", label: "Goals", icon: Target, testid: "nav-goals" },
  { to: "/coach", label: "Coach", icon: Sparkles, testid: "nav-coach" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-page)]">
      {/* Top bar */}
      <header className="bg-white border-b-2 border-black sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <div className="w-7 h-7 bg-[var(--alert-red)] flex items-center justify-center text-white font-display font-black text-sm">!</div>
            <div className="font-display font-black text-xl tracking-tighter">RED.FLAG</div>
            <div className="overline hidden sm:block">FINANCE TRACKER</div>
          </Link>
          <div className="flex items-center gap-4">
            {user?.is_premium ? (
              <div
                data-testid="premium-badge"
                className="hidden sm:flex items-center gap-1 bg-black text-white px-2 py-1 font-display font-bold text-xs tracking-widest"
              >
                <Crown size={12} /> PREMIUM
              </div>
            ) : (
              <Link
                to="/upgrade"
                data-testid="upgrade-link"
                className="hidden sm:flex items-center gap-1 bg-[var(--alert-red)] text-white px-3 py-2 font-display font-bold text-xs tracking-widest hover:bg-black transition-colors"
              >
                <Crown size={12} /> UPGRADE ₹200
              </Link>
            )}
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <div className="overline">USER</div>
              <div className="font-mono text-sm" data-testid="user-email">{user?.email}</div>
            </div>
            <button
              data-testid="logout-button"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="border-2 border-black px-3 py-2 hover:bg-black hover:text-white transition-colors flex items-center gap-2 font-display font-bold text-sm"
            >
              <LogOut size={16} /> LOGOUT
            </button>
          </div>
        </div>
      </header>

      {/* Sub nav */}
      <nav className="bg-white border-b border-black/15">
        <div className="max-w-[1400px] mx-auto px-6 flex overflow-x-auto">
          {NAV.map((item) => {
            const Active = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={`flex items-center gap-2 px-4 py-3 border-r border-black/15 font-display font-bold text-sm tracking-wide transition-colors ${
                  Active ? "bg-black text-white" : "hover:bg-black hover:text-white"
                }`}
              >
                <Icon size={16} />
                {item.label.toUpperCase()}
              </Link>
            );
          })}
        </div>
      </nav>

      <main className="flex-1 max-w-[1400px] w-full mx-auto px-6 py-8">{children}</main>

      <footer className="border-t border-black/15 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex justify-between items-center">
          <div className="overline">© RED.FLAG / KEEP IT IN CHECK</div>
          <div className="font-mono text-xs text-[var(--text-secondary)]">v1.0</div>
        </div>
      </footer>
    </div>
  );
}
